import { FC, useMemo } from 'react';

import { RoutePath } from '@common/constants';
import { useTranslation } from '@wearehelpful/ui-localization';
import {
  Typography,
  Content,
  Form,
  Skeleton,
  InfiniteScroll,
  Footer,
} from '@wearehelpful/ui-kit';
import { useRoute } from '@core/navigation';
import { useGetPlansInfinite } from '@helpful/network';

import { PlanForm } from '../Connect.types';
import { MultiPlanNotSuplimental } from '../../_partitions/MultiPlanNotSuplimental/MultiPlanNotSuplimental';
import { SinglePlanNotSuplimental } from '../../_partitions/SinglePlanNotSuplimental/SinglePlanNotSuplimental';
import { MultiPlanSupplemental } from '../../_partitions/MultiPlanSupplemental/MultiPlanSupplemental';
import { SinglePlanSuplimental } from '../../_partitions/SinglePlanSuplimental/SinglePlanSuplimental';
import { SinglePlanDefault } from '../../_partitions/SindlePlanDefault/SindlePlanDefault';

import { RouteSearch } from './Plan.types';
import { TAKE } from './Plan.const';

export const Plan: FC = () => {
  const { t } = useTranslation();

  const [
    {
      search: { id, zip, fips },
    },
    { push },
  ] = useRoute<{}, RouteSearch>();

  const { isLoading, data, hasNextPage, fetchNextPage } = useGetPlansInfinite(
    {
      skip: 0,
      take: TAKE,
      relations: 'payor',
      filter: { payorId: id, zip, fips },
    },
    {
      query: {
        getNextPageParam: ({ meta: { total } }, all) => {
          const loaded = all.flatMap(({ data }) => data).length;
          if (total > loaded) return TAKE * all.length;
        },
      },
    }
  );

  const plans = useMemo(
    () => data?.pages.flatMap(({ data }) => data) || [],
    [data]
  );

  const config = useMemo(() => plans[0]?.payor, [plans]);

  const defaultValues = useMemo(
    () => ({
      plan: (() => {
        if (!config?.isMultiSupplemental) {
          return plans?.find(({ isCompulsory }) => isCompulsory)?.id;
        }
      })(),
      plans: [],
      supplementals: (() => {
        if (config?.isMultiSupplemental) {
          return plans
            ?.filter(({ isCompulsory }) => isCompulsory)
            .map(({ id }) => id);
        }
        return [];
      })(),
    }),
    [plans, config]
  );

  const handleOnSubmit = (form: PlanForm) => {
    push({
      path: RoutePath.INSURANCE_CONNECT_DETAILS,
      search: { ...form, zip, fips },
    });
  };

  return (
    <Form<PlanForm> onSubmit={handleOnSubmit} defaultValues={defaultValues}>
      <Content>
        <Content.Subheader>
          <Typography variant="h2">
            {t('subheadings.insuranceConnectPlan.title', {
              payor: config?.name,
            })}
          </Typography>
        </Content.Subheader>
        <Content.Fixed>
          <Skeleton loading={isLoading} variant="list">
            <InfiniteScroll disabled={!hasNextPage} onScroll={fetchNextPage}>
              <MultiPlanNotSuplimental config={config} plans={plans} />
              <SinglePlanNotSuplimental config={config} plans={plans} />
              <MultiPlanSupplemental config={config} plans={plans} />
              <SinglePlanSuplimental config={config} plans={plans} />
              <SinglePlanDefault config={config} plans={plans} />
            </InfiniteScroll>
          </Skeleton>
        </Content.Fixed>
      </Content>
      <Footer>
        <Form.Submit checkValidity disabled={isLoading}>
          {t('actions.next')}
        </Form.Submit>
      </Footer>
    </Form>
  );
};
